import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { addCsrfHeader, fetchWithCSRF } from '../utils/csrfUtils';

export const useUserDietPreferences = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { sessionToken } = useAuth();

  const addUserDiet = async (dietId) => {
    setLoading(true);
    setError(null);
    
    try {
      if (!sessionToken) {
        throw new Error('No valid session found');
      }
      
      const response = await fetchWithCSRF(`${process.env.REACT_APP_POSTGRES_API_ENDPOINT}/user-diet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
                  body: JSON.stringify({
            diet_id: dietId
          })
      });

      if (!response.ok) {
        throw new Error('Failed to add diet preference');
      }

      return await response.json();

    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteUserDiet = async (dietId) => {
    setLoading(true);
    setError(null);

    try {
      if (!sessionToken) {
        throw new Error('No valid session found');
      }
      
      const response = await fetchWithCSRF(
        `${process.env.REACT_APP_POSTGRES_API_ENDPOINT}/user-diet/diet/${dietId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete diet preference');
      }

      return true;

    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    addUserDiet,
    deleteUserDiet,
    error,
    loading
  };
};

export const useUserCuisinePreferences = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { sessionToken } = useAuth();

  const addUserCuisine = async (cuisineId) => {
    setLoading(true);
    setError(null);
    
    try {
      if (!sessionToken) {
        throw new Error('No valid session found');
      }
      
      // Add CSRF token to headers along with auth token
      const headers = await addCsrfHeader({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`
      });
      
      const response = await fetchWithCSRF(`${process.env.REACT_APP_POSTGRES_API_ENDPOINT}/user-cuisine`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          cuisine_id: cuisineId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add cuisine preference');
      }

      return await response.json();

    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteUserCuisine = async (cuisineId) => {
    setLoading(true);
    setError(null);

    try {
      if (!sessionToken) {
        throw new Error('No valid session found');
      }
      
      // Add CSRF token to headers along with auth token
      const headers = await addCsrfHeader({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`
      });
      
      const response = await fetchWithCSRF(
        `${process.env.REACT_APP_POSTGRES_API_ENDPOINT}/user-cuisine/cuisine/${cuisineId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete cuisine preference');
      }

      return true;

    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    addUserCuisine,
    deleteUserCuisine,
    error,
    loading
  };
};

export const useUserAllergenPreferences = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { sessionToken } = useAuth();

  const addUserAllergen = async (ingredientId) => {
    setLoading(true);
    setError(null);
    
    try {
      if (!sessionToken) {
        throw new Error('No valid session found');
      }
      
      const response = await fetchWithCSRF(`${process.env.REACT_APP_POSTGRES_API_ENDPOINT}/user-allergen-ingredient`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          ingredient_id: ingredientId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add allergen preference');
      }

      return await response.json();

    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteUserAllergen = async (ingredientId) => {
    setLoading(true);
    setError(null);

    try {
      if (!sessionToken) {
        throw new Error('No valid session found');
      }
      
      const response = await fetchWithCSRF(
        `${process.env.REACT_APP_POSTGRES_API_ENDPOINT}/user-allergen-ingredient/ingredient/${ingredientId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete allergen preference');
      }

      return true;

    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    addUserAllergen,
    deleteUserAllergen,
    error,
    loading
  };
};

export const useUserMealPlanPreferences = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { sessionToken } = useAuth();

  const updateUserMealPlanPreferences = async (appUserId, preferences) => {
    setLoading(true);
    setError(null);
    
    try {
      if (!sessionToken) {
          throw new Error('No valid session found');
      }

      const response = await fetchWithCSRF(`${process.env.REACT_APP_POSTGRES_API_ENDPOINT}/users/meal_plan_preferences`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          number_of_breakfast_meals: parseInt(preferences.number_of_breakfast_meals),
          number_of_lunch_meals: parseInt(preferences.number_of_lunch_meals),
          number_of_dinner_meals: parseInt(preferences.number_of_dinner_meals),
          breakfast_serving_size: parseInt(preferences.breakfast_serving_size),
          lunch_serving_size: parseInt(preferences.lunch_serving_size),
          dinner_serving_size: parseInt(preferences.dinner_serving_size)
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error('Failed to update meal plan preferences - body: ' + JSON.stringify(preferences));
      }

      return await response.json();

    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateUserMealPlanPreferences,
    error,
    loading
  };
};

export const useUserShowMealDeletionConfirmation = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { sessionToken } = useAuth();

  const updateUserShowMealDeletionConfirmation = async (showMealDeletionConfirmationModal) => {
    setLoading(true);
    setError(null);
    
    try {
      if (!sessionToken) {
        throw new Error('No valid session found');
      }

      const response = await fetchWithCSRF(`${process.env.REACT_APP_POSTGRES_API_ENDPOINT}/users/show_meal_deletion_confirmation_modal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          show_meal_deletion_confirmation_modal: showMealDeletionConfirmationModal
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error('Failed to update meal deletion confirmation preference');
      }

      return await response.json();

    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateUserShowMealDeletionConfirmation,
    error,
    loading
  };
};

export default useUserDietPreferences;
